/**
 * WS-C §3.4 — forced rotation.
 *
 * ⛔ `mustChangePassword` OUTRANKS EVERY OTHER GATE, and that is a security requirement, not a UX
 * preference. An operator whose password we are *forcing* them to rotate is, by definition, one
 * whose current credential we no longer trust — so letting them reach the shell first makes the
 * compelled rotation cosmetic. `CpAdminApp` enforces the ordering (branch 3 above branch 4); this
 * page is what that branch renders for EVERY path.
 *
 * ⚠️ The old console records the sharpest form of this: approving an OAuth grant before rotating
 * would mint a delegated token that SURVIVES the password change, so whoever holds the old
 * password walks away with a credential the new password does not revoke.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { displayMessage } from '../platform/errors';
import { takeReturnTo as takeReturnToDefault } from '../platform/auth/return-to';
import { AuthCardFrame, AuthField } from '../shell/AuthCardFrame';
import { Button } from '../shell/Button';

export interface ChangePasswordPageProps {
  /** ⚠️ Resolves with a NEW token pair, which the caller must adopt — the old token still
   *  carries `mustChangePassword`, so failing to adopt it loops straight back to this page. */
  readonly onChangePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /**
   * Consumes the §4 stash exactly once, ROUTER-RELATIVE. Same seam as `OAuthCallbackPage`.
   *
   * ⚠️ A forced rotation is an INTERRUPTION like an expired session, so it must resume the
   * destination too. The old console does NOT do this — it lands every rotating operator on their
   * section landing and its own comment records that as accepted-not-oversight. Here the stash
   * already exists and consuming it costs one line, so there is no reason to inherit the gap.
   */
  readonly takeReturnTo?: () => string | null;
}

export function ChangePasswordPage({
  onChangePassword,
  takeReturnTo = takeReturnToDefault,
}: ChangePasswordPageProps): React.JSX.Element {
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    /*
     * ⛔ The confirm-match check is CLIENT-SIDE ONLY and is not a substitute for anything. The
     * server owns every strength rule; this catches the one class of mistake the server cannot
     * see — a typo in a field the operator cannot read back — and it does so BEFORE spending a
     * request, so a mistyped confirmation does not consume a rate-limit slot.
     */
    if (next !== confirm) {
      setError('The new password and its confirmation do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onChangePassword(current, next);
      // ⛔ Router-relative on both arms — see `takeReturnTo`'s docblock. Adopting the new token pair
      // is what clears `mustChangePassword`; this only chooses where the now-unblocked shell opens.
      void navigate(takeReturnTo() ?? '/', { replace: true });
    } catch (e: unknown) {
      // Every field is cleared on failure, unlike the login form's email. Here all three inputs
      // are secrets and none of them is a convenience to retain.
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(displayMessage(e, 'Could not change your password.'));
    } finally {
      setBusy(false);
    }
  }

  /*
   * ⚠️ **THE AMBER CARD BORDER AND THE `ShieldAlert` ARE GONE, AND THAT IS THE RULING RATHER THAN A
   * LOSS.** This card was the only one of the three on `border-state-warning/30`; the other two
   * disagreed with each other too (`border-border-subtle` / `border-state-critical/30`), which is what
   * `AuthCardFrame` exists to end — three copies of one layout drift by definition. The *fact* the
   * amber was carrying is now carried in words, by the title and the sentence below it, which is where
   * WCAG 1.4.1 wants it anyway: a colour was never the only channel a forced rotation had.
   *
   * ⛔ The explanation is the frame's `subtitle`, NOT a `caution` notice, so it stays on screen when a
   * failure arrives. The `notice` slot holds exactly one thing at a time, and an error must not have to
   * evict the reason the operator is on this page.
   */
  return (
    <AuthCardFrame
      title="Choose a new password"
      subtitle="Your account requires a password change before you can continue."
      notice={error === null ? undefined : { verdict: 'bad', message: error }}
      testId="change-password"
    >
      {/* ⛔ The `<form>` is now INSIDE the card rather than being it — the submit button stays within
          the form element, which is what keeps Enter-to-submit working from any of the three fields. */}
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
        <AuthField
          label="Current password"
          type="password"
          name="current-password"
          autoComplete="current-password"
          value={current}
          onChange={setCurrent}
          testId="change-password-current"
        />
        <AuthField
          label="New password"
          type="password"
          name="new-password"
          autoComplete="new-password"
          value={next}
          onChange={setNext}
          testId="change-password-new"
        />
        <AuthField
          label="Confirm new password"
          type="password"
          name="confirm-new-password"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          testId="change-password-confirm"
        />
        <Button type="submit" tone="primary" size="md" disabled={busy} data-testid="change-password-submit">
          Change password
        </Button>
      </form>
    </AuthCardFrame>
  );
}
